import { Component, Input, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ICamOperasyon } from './fiko-cam.types';
import { FikoToolAxis3DViewer } from './fiko-toolaxis-3d.component';

/**
 * FikoYT CAM — Takım Ekseni Tanımı (CATIA "Tool Axis Definition" muadili).
 * IJK düzenlenebilir · izometrik triad · hızlı eksen · ters çevir · yüzeye bağlı (associative parametrik).
 */
@Component({
  selector: 'fiko-cam-tool-axis-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, FikoToolAxis3DViewer],
  template: `
    <div class="ta-kok">
      <div class="ta-bsl">🧭 Takım Ekseni Tanımı
        <span class="ta-mod" [class.bagli]="bagliMi()">{{ bagliMi() ? '🔗 Yüzeye Bağlı (parametrik)' : 'Sabit' }}</span>
      </div>

      <div class="ta-orta">
        <!-- İnteraktif 3D triad — X/Y/Z oka tıkla → eksen · takıma tıkla → yüzeyden seç -->
        <fiko-toolaxis-3d
          [ijk]="{ x: i, y: j, z: k }"
          [bagli]="bagliMi()"
          [aktifEksen]="taYon()"
          (eksenSec)="eksenOkTik($event)"
          (yuzeyTikla)="yuzeySec()">
        </fiko-toolaxis-3d>
        @if(yuzeyAktif()){ <div class="tax-secmetin-3d">🎯 3D sahnede yüze tıkla…</div> }

        <!-- IJK alt alta -->
        <div class="ta-ijk-bsl">Eksen Vektörü · oka/takıma tıkla</div>
        <div class="ta-ijk">
          <label><span style="color:#f87171">I</span><input type="number" step="0.01" [ngModel]="i" (ngModelChange)="setIJK('x',$event)"/></label>
          <label><span style="color:#34d399">J</span><input type="number" step="0.01" [ngModel]="j" (ngModelChange)="setIJK('y',$event)"/></label>
          <label><span style="color:#38bdf8">K</span><input type="number" step="0.01" [ngModel]="k" (ngModelChange)="setIJK('z',$event)"/></label>
        </div>
        <button class="ta-ters" (click)="ters()" title="Yönü ters çevir">⇅ Yönü Ters Çevir</button>
        @if(bagliMi()){ <div class="ta-bilgi">🔗 Yüzey #{{ ty?.yuzId }} normaline bağlı — parça değişince eksen güncellenir.</div> }
      </div>

      <div class="ta-alt">
        <div class="ta-etik">Aktif: <b>{{ etiket() }}</b></div>
        <button class="ta-kapat" (click)="kapat()">Kapat</button>
      </div>
    </div>
  `,
  styles: [`
    .ta-kok{display:flex;flex-direction:column;gap:0;color:#dbeafe;font-family:system-ui;width:212px;}
    .ta-bsl{display:flex;flex-direction:column;align-items:flex-start;gap:2px;padding:5px 8px;font:700 10px system-ui;color:#bcd9ff;border-bottom:1px solid rgba(56,189,248,.2);}
    .ta-mod{font:600 8px system-ui;color:#cbd5e1;background:rgba(120,140,170,.18);border-radius:5px;padding:1px 6px;}
    .ta-mod.bagli{color:#7fffd4;background:rgba(45,212,191,.16);border:1px solid rgba(45,212,191,.45);}
    .ta-orta{display:flex;flex-direction:column;gap:4px;padding:6px;}
    .tax-secmetin-3d{text-align:center;color:#7ee0ff;font:700 9px system-ui;animation:taxPulse 1s infinite;}
    @keyframes taxPulse{0%,100%{opacity:.45}50%{opacity:1}}
    .ta-ijk-bsl{font:600 8px system-ui;color:#9fc6ff;}
    .ta-ijk{display:flex;flex-direction:column;gap:3px;}
    .ta-ijk label{display:flex;align-items:center;gap:5px;background:rgba(10,20,35,.5);border:1px solid rgba(56,189,248,.22);border-radius:5px;padding:2px 6px;font:800 9px system-ui;}
    .ta-ijk input{width:100%;background:transparent;border:none;color:#fff;font:700 10px system-ui;font-variant-numeric:tabular-nums;text-align:right;outline:none;}
    .ta-ters{background:rgba(10,20,35,.55);border:1px solid rgba(252,211,77,.4);border-radius:5px;color:#fcd34d;font:700 9px system-ui;padding:4px 0;cursor:pointer;transition:.15s;}
    .ta-ters:hover{background:rgba(60,50,20,.45);}
    .ta-bilgi{font:600 8px system-ui;color:#9fe8d0;background:rgba(45,212,191,.1);border-radius:5px;padding:4px 6px;line-height:1.35;}
    .ta-alt{display:flex;justify-content:space-between;align-items:center;padding:4px 8px;border-top:1px solid rgba(56,189,248,.18);}
    .ta-etik{font:600 9px system-ui;color:#9be8b0;}
    .ta-kapat{background:linear-gradient(135deg,rgba(22,135,90,.6),rgba(18,110,72,.55));border:1px solid rgba(52,211,153,.55);border-radius:6px;color:#a7f3d0;font:700 10px system-ui;padding:4px 12px;cursor:pointer;}
    .ta-kapat:hover{filter:brightness(1.15);}
  `]
})
export class FikoCamToolAxisDialog {
  @Input() element: any;
  @Input() yonetici: any;
  @Input() pencere: any;
  constructor(public CD: ChangeDetectorRef) {}
  get op(): ICamOperasyon { return this.element; }
  get yon() { return this.yonetici; }
  get ty(): any { return this.op?.takimYonu; }

  get i(): number { return +((this.ty?.yon?.x ?? 0)).toFixed(5); }
  get j(): number { return +((this.ty?.yon?.y ?? 0)).toFixed(5); }
  get k(): number { return +((this.ty?.yon?.z ?? 1)).toFixed(5); }
  setIJK(ax: 'x' | 'y' | 'z', v: any) {
    const n = +v; if (!Number.isFinite(n)) return;
    const cur: any = { x: this.i, y: this.j, z: this.k }; cur[ax] = n;
    if (Math.hypot(cur.x, cur.y, cur.z) < 1e-6) return;
    this.yon?.takimYonuAyarla?.(this.op, { yon: cur, kaynak: 'custom' });   // manuel IJK → bağlantı kopar
    this.CD.markForCheck();
  }
  taYon(): 'x' | 'y' | 'z' | 'yuzey' | 'custom' {
    const ty = this.ty; if (!ty?.yon) return 'z';
    if (ty.kaynak === 'yuzey') return 'yuzey';
    const y = ty.yon;
    if (Math.abs(y.z) > 0.999) return 'z';
    if (Math.abs(y.x) > 0.999) return 'x';
    if (Math.abs(y.y) > 0.999) return 'y';
    return 'custom';
  }
  etiket(): string { return this.yon?.takimYonuEtiket?.(this.op) ?? 'Z'; }
  hizliEksen(d: 'xy' | 'xz' | 'yz') { this.yon?.takimYonuDuzlem?.(this.op, d); this.CD.markForCheck(); }
  /** 3D triad'da eksen okuna tıklandı: X→YZ düzlemine dik, Y→XZ, Z→XY. */
  eksenOkTik(eks: 'x' | 'y' | 'z') { this.hizliEksen(eks === 'x' ? 'yz' : eks === 'y' ? 'xz' : 'xy'); }
  ters() {
    const c = { x: -this.i, y: -this.j, z: -this.k };
    this.yon?.takimYonuAyarla?.(this.op, { yon: c, kaynak: 'custom' });   // ters → sabit (bağlantı kopar)
    this.CD.markForCheck();
  }
  yuzeySec() { this.yon?.camTakimYonuYuzeySecBaslat?.(this.op, () => this.CD.markForCheck()); this.CD.markForCheck(); }
  yuzeyAktif(): boolean { return !!(this.yon as any)?.camToolAxisSecAktif; }
  bagliMi(): boolean { return this.ty?.kaynak === 'yuzey' && this.ty?.yuzId != null; }
  kapat() { this.pencere?.kapat?.(); }
}
